/* eslint-disable @typescript-eslint/prefer-nullish-coalescing */
/**
 * @author       esraa.afifi@zumper.com (Esraa Afifi)
 * @copyright    Copyright (c) 2022, Zumper
 */

import React, { memo, useMemo } from 'react'
import { CarouselInternalState } from 'react-multi-carousel'
import { twMerge } from 'tailwind-merge'

export interface ProgressDotsProps extends Partial<CarouselInternalState> {
  disabled?: (index: number) => boolean
  isMobile?: boolean
  firstVisibleDotIndex: number
  goToSlide: ((index: number) => void) | undefined
  totalItems: number
  currentSlide: number
  maxDots?: number
}

export const ProgressDots = memo(
  ({
    disabled,
    isMobile,
    firstVisibleDotIndex,
    goToSlide,
    totalItems,
    maxDots,
    currentSlide,
  }: ProgressDotsProps) => {
    const indexOffset = useMemo(
      () =>
        maxDots && totalItems > maxDots
          ? Math.floor(currentSlide / maxDots) * maxDots
          : 0,
      [maxDots, totalItems, currentSlide]
    )

    const offsetExists = indexOffset !== undefined
    const maxIndex = maxDots ?? totalItems

    const indexArray = useMemo(
      () => Array.from(Array(maxIndex).keys()),
      [maxIndex]
    )

    return (
      <ul
        className={twMerge(
          'z-[1] flex flex-row items-center justify-center m-0 p-0 list-none gap-1',
          offsetExists && 'justify-start'
        )}
        style={{
          transform: `translateX(${
            (totalItems > 4 ? 4 : 0) + -10 * firstVisibleDotIndex
          }px)`,
        }}
      >
        {indexArray.map((index) => {
          const activeDot = offsetExists
            ? indexOffset + index === currentSlide
            : currentSlide === index

          const calcIndex =
            indexOffset && currentSlide >= maxIndex
              ? index + indexOffset
              : index

          if (calcIndex >= totalItems) {
            return null
          }
          const isNotLast = index !== totalItems - 1
          const isNotFirst = index !== 0
          let dotClass = ''

          if (index !== totalItems - 1) {
            if (
              (index === 3 + firstVisibleDotIndex && isNotLast) ||
              (index === firstVisibleDotIndex && isNotFirst)
            ) {
              dotClass += ' scale-[0.7]'
            }
            if (
              index >= 4 + firstVisibleDotIndex ||
              index < firstVisibleDotIndex
            ) {
              dotClass += ' scale-[0.25]'
            }
          }

          return (
            <li
              className={twMerge('flex items-center justify-center', dotClass)}
              key={`dot-${calcIndex}`}
            >
              <button
                aria-label={`slide ${String(calcIndex + 1)} of ${String(
                  totalItems
                )}`}
                className="size-[6px] rounded-full p-0 border-0 cursor-pointer"
                disabled={isMobile || disabled?.(calcIndex) || !goToSlide}
                onClick={(e) => {
                  e.stopPropagation()
                  goToSlide?.(calcIndex)
                }}
                style={{
                  backgroundColor: activeDot ? '#FFFFFF' : '#FFFFFFB3',
                }}
                type="button"
              >
                {activeDot && <span className="sr-only">Current Item</span>}
                <span className="sr-only">{calcIndex + 1}</span>
              </button>
            </li>
          )
        })}
      </ul>
    )
  }
)
